/**
 * Precision Mandate — the memo record as a printed document.
 *
 * Replaces the old popup-and-window.print() export, which produced an
 * unbranded page in whatever fonts and margins the reader's browser felt like
 * using, with a real PDF the platform controls end to end.
 *
 * The visual language is the one the memo view already speaks: white paper,
 * hairline rules, generous whitespace, tinted status pills, and a person shown
 * as an initials avatar in a color derived from their name — the same hash the
 * screen uses, so the same teammate is the same color in both places.
 * Organization branding comes from the Deck Studio brand store: the client's
 * logo on the masthead and their name on every page.
 *
 * CONTENT RULES
 *
 * Everything the memo view shows goes in, because the operator rule for this
 * module is that the work is always visible. That means the briefing, the
 * enforcement risk, the summary, every action with its owner, department,
 * supporting departments, applicability, instructions, verified source quote,
 * evidence, and the discussion threads that hang off it, plus the memo-level
 * discussion and the documents on file. A section with nothing in it prints
 * "None on file" so the reader can tell the difference between considered and
 * skipped.
 *
 * No mention of agents anywhere in the document. A memo record can leave the
 * building through the auditor link, and by the time an analysis is adopted
 * into the record it is the organization's own work product.
 *
 * Every network call here is optional. A missing logo, an unreachable
 * organization endpoint, or a branding table that predates the migration
 * degrades to a complete document without that element, never to a failed
 * export.
 */

import { jsPDF } from 'jspdf';

// ── Page geometry, in points. Letter portrait. ──────────────────────────────
const PAGE_W = 612;
const PAGE_H = 792;
const M = 54;                      // page margin, 0.75in
const CONTENT_W = PAGE_W - M * 2;  // 504
const HEADER_RULE_Y = 104;         // masthead hairline, first page
const FIRST_TOP = 132;             // where first-page content begins
const CONT_TOP = 84;               // where continuation-page content begins
const BODY_BOTTOM = PAGE_H - 62;   // last y a body line may occupy

// ── Palette. Platform navy and orange over Asana-flat neutrals. ─────────────
const NAVY = [14, 40, 65];         // #0E2841 — headings, the platform ink
const INK = [30, 41, 59];          // body emphasis
const BODY = [71, 85, 105];        // running text
const MUTED = [124, 136, 150];     // labels, meta
const FAINT = [168, 179, 189];     // timestamps
const RULE = [226, 232, 240];      // hairlines
const HAIR = [239, 243, 247];      // lighter hairlines, inside cards
const PANEL = [248, 250, 252];     // quiet fills
const CARD = [252, 253, 254];      // action card fill
const WHITE = [255, 255, 255];
const BRAND = [245, 130, 32];      // #F58220 — the single accent

const AMBER_BG = [254, 247, 232];
const AMBER_LINE = [251, 224, 175];
const AMBER_INK = [146, 64, 14];
const GREEN_BG = [236, 250, 243];
const GREEN_INK = [4, 120, 87];
const ROSE_BG = [254, 238, 241];
const ROSE_INK = [190, 18, 60];

// Memo status → [label, fill, ink]. Mirrors STATUS_PILL/STATUS_LABEL on screen.
const MEMO_STATUS = {
  analyzing: ['Analyzing', [244, 240, 254], [109, 40, 217]],
  failed: ['Analysis failed', ROSE_BG, ROSE_INK],
  open: ['In progress', [235, 243, 254], [29, 78, 216]],
  'pending-review': ['Pending review', AMBER_BG, [180, 83, 9]],
  completed: ['Completed', GREEN_BG, GREEN_INK],
  retired: ['Retired', [241, 245, 249], [100, 116, 139]],
};

// Classification → [fill, ink]. Mirrors CLS_PILL on screen.
const CLASS_PILL = {
  Actionable: [[255, 244, 234], [194, 65, 12]],
  Informational: [[234, 245, 253], [3, 105, 161]],
};

// Action status → [label, ink, fill]. Mirrors ACTION_STATUS on screen.
const ACT_STATUS = {
  pending: ['Pending', [100, 116, 139], [241, 245, 249]],
  'in-progress': ['In progress', [29, 78, 216], [235, 243, 254]],
  submitted: ['Submitted · with compliance', [180, 83, 9], AMBER_BG],
  returned: ['Returned · needs rework', ROSE_INK, ROSE_BG],
  approved: ['Approved', GREEN_INK, GREEN_BG],
  'not-applicable': ['Not applicable', [148, 163, 184], [241, 245, 249]],
};

// The on-screen AVATAR_COLORS, as RGB. Index selection uses the identical hash
// so a person's color is stable between the screen and the printed record.
const AVATAR_RGB = [
  [249, 115, 22],  // orange-500
  [14, 165, 233],  // sky-500
  [16, 185, 129],  // emerald-500
  [139, 92, 246],  // violet-500
  [244, 63, 94],   // rose-500
  [245, 158, 11],  // amber-500
  [13, 148, 136],  // teal-600
  [99, 102, 241],  // indigo-500
];

const NA = 'None on file';

// ---------------------------------------------------------------------------
// Text hygiene
// ---------------------------------------------------------------------------

// jsPDF's built-in fonts encode cp1252. These characters live above U+00FF but
// are representable, so they survive; anything else outside the range would
// draw as garbage and is either mapped to an ASCII equivalent or dropped.
const CP1252_EXTRA = new Set([
  '€', '‚', 'ƒ', '„', '…', '†', '‡', 'ˆ',
  '‰', 'Š', '‹', 'Œ', 'Ž', '‘', '’', '“',
  '”', '•', '–', '—', '˜', '™', 'š', '›',
  'œ', 'ž', 'Ÿ',
]);

// A sentinel that cannot occur in memo text, marking where a character was
// dropped so the surrounding whitespace can be closed up.
const DROPPED = String.fromCharCode(0);
const DROPPED_RUN = new RegExp(` ?${DROPPED}+ ?`, 'g');

export function pdfSafe(v) {
  if (v == null) return '';
  const mapped = String(v)
    .replace(/\r\n?/g, '\n')
    .replace(/ /g, ' ')
    .replace(/[‑‒−]/g, '-')
    .replace(/[→⇒]/g, '->')
    .replace(/[←⇐]/g, '<-')
    .replace(/[✓✔]/g, '+')
    .replace(/[✕✖×]/g, 'x');
  // Unrepresentable characters are marked rather than removed outright, so the
  // space that surrounded them can be closed up afterwards: dropping an emoji
  // between two words would otherwise leave a gap in the middle of a sentence.
  let out = '';
  for (const ch of mapped) {
    const c = ch.codePointAt(0);
    if (ch === '\n' || (c >= 32 && c <= 255) || CP1252_EXTRA.has(ch)) out += ch;
    else out += DROPPED;
  }
  return out.replace(DROPPED_RUN, (m) => (m.startsWith(' ') && m.endsWith(' ') ? ' ' : ''));
}

const has = (v) => v != null && String(v).trim() !== '';
const txt = (v) => pdfSafe(String(v == null ? '' : v).trim());

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * A calendar date, printed as the calendar date it is.
 *
 * received_date and external_due_date are Postgres DATE columns: a day with no
 * time and no zone in it. They reach the browser either bare or as UTC
 * midnight, and running either through the reader's local timezone moves a due
 * date a full day whenever the reader sits west of the server, which is every
 * user of a UTC container. Both shapes are therefore read as calendar
 * components and never localized. Real timestamps, which do carry a moment in
 * time, still localize normally.
 */
export function fmtDay(d) {
  if (!d) return '';
  const s = typeof d === 'string'
    ? d
    : (d instanceof Date ? d.toISOString() : String(d));
  const cal = /^(\d{4})-(\d{2})-(\d{2})(?:T00:00:00(?:\.000)?Z)?$/.exec(s);
  if (cal) return `${MONTHS[Number(cal[2]) - 1]} ${Number(cal[3])}, ${cal[1]}`;
  const dt = new Date(s);
  if (Number.isNaN(dt.getTime())) return String(d).slice(0, 10);
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function fmtWhen(d) {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

export function initialsOf(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

/** The screen's hash, character for character, so colors agree. */
export function avatarRgb(name) {
  let h = 0;
  for (const ch of String(name || '')) h = ((h * 31) + ch.charCodeAt(0)) >>> 0;
  return AVATAR_RGB[h % AVATAR_RGB.length];
}

const humanize = (v) => (has(v) ? String(v).replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()) : '');

const sizeLabel = (bytes) => (bytes ? `${Math.max(1, Math.round(bytes / 1024))} KB` : '');

// ---------------------------------------------------------------------------
// Model — the memo, flattened into exactly what the page draws.
// ---------------------------------------------------------------------------

export function buildMemoModel({
  memo, actions = [], comments = [], documents = [],
  orgName = '', logoDataUrl = null, generatedBy = '',
}) {
  const memoComments = comments.filter((c) => !c.action_id);
  const approved = actions.filter((a) => a.status === 'approved').length;
  const recipients = Array.isArray(memo.recipients) ? memo.recipients : [];
  const acked = recipients.filter((r) => r.acknowledged_at).length;

  const glance = [
    { label: 'Received', value: fmtDay(memo.received_date) || 'Not stated' },
    { label: 'Due', value: fmtDay(memo.external_due_date) || 'None stated' },
    { label: 'Urgency', value: humanize(memo.urgency) || 'Not stated' },
    {
      label: 'Memo owner',
      value: has(memo.owner_name)
        ? txt(memo.owner_name) + (has(memo.owner_department_name) ? `\n${txt(memo.owner_department_name)}` : '')
        : 'Unassigned',
    },
  ];
  if (recipients.length > 0) {
    glance.push({ label: 'Acknowledged', value: `${acked} of ${recipients.length}` });
  }

  const mapComment = (c) => ({
    author: txt(c.author) || 'Former teammate',
    initials: initialsOf(c.author),
    color: avatarRgb(c.author),
    when: fmtWhen(c.created_at),
    body: txt(c.body),
  });

  return {
    orgName: txt(orgName),
    logoDataUrl,
    generatedAt: fmtDay(new Date()),
    generatedBy: txt(generatedBy),

    memoNumber: txt(memo.memo_number),
    title: txt(memo.title) || 'Untitled memo',
    classification: txt(memo.classification),
    status: memo.status,
    glance,

    briefing: has(memo.briefing?.headline)
      ? [txt(memo.briefing.headline), txt(memo.briefing.whatItMeans)].filter(Boolean).join(' ')
      : null,
    risk: has(memo.briefing?.enforcementRisk) ? txt(memo.briefing.enforcementRisk) : null,
    summary: (Array.isArray(memo.summary) ? memo.summary : []).map(txt).filter(Boolean),

    approved,
    actions: actions.map((a) => ({
      n: (a.position ?? 0) + 1,
      title: txt(a.action) || 'Untitled action',
      status: a.status,
      due: fmtDay(a.external_due_date),
      owner: has(a.owner_name) ? txt(a.owner_name) : null,
      ownerInitials: initialsOf(a.owner_name),
      ownerColor: avatarRgb(a.owner_name),
      department: txt(a.department_name),
      supporting: (Array.isArray(a.supporting_departments) ? a.supporting_departments : []).map(txt).filter(Boolean),
      applicability: a.applicability && a.applicability !== 'mandatory' ? humanize(a.applicability) : null,
      appliesIf: has(a.applies_if) ? txt(a.applies_if) : null,
      whatToDo: has(a.what_to_do) ? txt(a.what_to_do) : null,
      quote: has(a.source_quote) ? txt(a.source_quote) : null,
      quotePage: a.source_page ? String(a.source_page) : null,
      evidenceHint: has(a.evidence_hint) ? txt(a.evidence_hint) : null,
      evidence: (Array.isArray(a.evidence) ? a.evidence : []).map((e) => txt(e.file_name)).filter(Boolean),
      alsoOn: (Array.isArray(a.assignees) ? a.assignees : [])
        .filter((x) => x.name && x.name !== a.owner_name).map((x) => txt(x.name)).filter(Boolean),
      comments: comments.filter((c) => c.action_id === a.id).map(mapComment),
    })),

    // The screen shows the memo-level thread as the discussion when a memo has
    // no actions, and as carried-over history when it does. The record keeps
    // that distinction so a reader knows which they are looking at.
    discussion: memoComments.length > 0 || actions.length === 0
      ? {
        label: actions.length === 0 ? 'Discussion' : 'Carried over from the previous Mandate',
        comments: memoComments.map(mapComment),
      }
      : null,

    documents: documents.map((d) => ({
      name: txt(d.file_name),
      isSource: d.kind === 'source',
      size: sizeLabel(d.size_bytes),
    })),
  };
}

// ---------------------------------------------------------------------------
// Renderer
// ---------------------------------------------------------------------------

export function renderMemoPdf(model) {
  const doc = new jsPDF({ unit: 'pt', format: 'letter', compress: true });

  // Every visual operation goes through the pen. In dry mode it advances the
  // cursor without marking the page, which is how a block is measured with the
  // exact code that later draws it — the measurement can never drift from the
  // drawing, because they are the same pass.
  const pen = {
    y: FIRST_TOP,
    dry: false,
    breaks: true,
  };

  const face = (style = 'normal', size = 9.5) => { doc.setFont('helvetica', style); doc.setFontSize(size); };
  const ink = (c) => doc.setTextColor(c[0], c[1], c[2]);
  const fill = (c) => doc.setFillColor(c[0], c[1], c[2]);
  const stroke = (c) => doc.setDrawColor(c[0], c[1], c[2]);

  const write = (t, x, y, opts) => { if (!pen.dry) doc.text(t, x, y, opts); };
  const rbox =(x, y, w, h, r, style = 'F') => { if (!pen.dry) doc.roundedRect(x, y, w, h, r, r, style); };
  const dot = (x, y, r, style = 'F') => { if (!pen.dry) doc.circle(x, y, r, style); };
  const rule = (x1, y1, x2, y2) => { if (!pen.dry) doc.line(x1, y1, x2, y2); };

  const lh = (size) => size * 1.42;
  const wrap = (t, w) => doc.splitTextToSize(t || '', w);

  const newPage = () => { doc.addPage(); pen.y = CONT_TOP; };

  /** Break to a new page when the next block will not fit. */
  const room = (needed) => {
    if (!pen.breaks || pen.dry) return;
    if (pen.y + needed > BODY_BOTTOM) newPage();
  };

  /**
   * Wrapped paragraph. Breaks between lines rather than orphaning them, so a
   * long instruction flows across pages without losing a line to the gutter.
   */
  const para = (t, x, w, { size = 9.5, style = 'normal', color = BODY, gap = 0 } = {}) => {
    if (!has(t)) return;
    face(style, size);
    ink(color);
    const step = lh(size);
    for (const line of wrap(t, w)) {
      room(step);
      write(line, x, pen.y + size * 0.82);
      pen.y += step;
    }
    pen.y += gap;
  };

  /**
   * Small uppercase section label with a hairline beneath it. `follow` is how
   * much of what comes next must fit on the same page: without it a heading
   * lands in the last inch of a page and its content starts on the next one.
   */
  const section = (label, follow = 30) => {
    room(46 + follow);
    face('bold', 7.5);
    ink(MUTED);
    write(String(label).toUpperCase(), M, pen.y + 7, { charSpace: 1.1 });
    pen.y += 13;
    stroke(RULE);
    doc.setLineWidth(0.75);
    rule(M, pen.y, M + CONTENT_W, pen.y);
    pen.y += 13;
  };

  /** A tinted rounded pill. Returns the width it consumed. */
  const pill = (label, x, y, fillC, inkC, { size = 8 } = {}) => {
    face('bold', size);
    const w = doc.getTextWidth(label) + 15;
    const h = size + 8;
    fill(fillC);
    rbox(x, y, w, h, h / 2);
    ink(inkC);
    write(label, x + 7.5, y + h - (h - size) / 2 - size * 0.19);
    return w + 6;
  };

  /** Initials avatar, the screen's circle at print scale. */
  const avatar = (initials, color, cx, cy, r = 8.5) => {
    fill(color);
    dot(cx, cy, r);
    face('bold', r * 0.86);
    ink(WHITE);
    write(initials, cx, cy + r * 0.32, { align: 'center' });
  };

  const emptyLine = () => {
    face('italic', 9);
    ink(FAINT);
    room(lh(9));
    write(NA, M, pen.y + 7.4);
    pen.y += lh(9) + 4;
  };

  // ── Masthead ──────────────────────────────────────────────────────────────
  const masthead = () => {
    let namedInHeader = false;
    if (model.logoDataUrl) {
      // Fit inside a fixed box preserving the logo's own proportions. A
      // wordmark and a square mark both come out looking deliberate.
      const maxW = 150;
      const maxH = 32;
      let w = maxW;
      let h = maxH;
      try {
        const props = doc.getImageProperties(model.logoDataUrl);
        if (props?.width && props?.height) {
          const scale = Math.min(maxW / props.width, maxH / props.height);
          w = props.width * scale;
          h = props.height * scale;
        }
      } catch { /* proportions unavailable — the fixed box still prints */ }
      try {
        if (!pen.dry) doc.addImage(model.logoDataUrl, undefined, M, 74 - h, w, h, undefined, 'FAST');
        namedInHeader = true;
      } catch { /* a logo that will not decode never blocks the export */ }
    }

    if (namedInHeader) {
      if (model.orgName) {
        face('normal', 8.5);
        ink(MUTED);
        write(model.orgName, M, 92);
      }
    } else {
      face('bold', 13);
      ink(NAVY);
      write(model.orgName || 'Memo record', M, 74);
    }

    const right = M + CONTENT_W;
    face('bold', 7.5);
    ink(BRAND);
    write('MEMO RECORD', right, 58, { align: 'right', charSpace: 1.2 });
    face('normal', 7.5);
    ink(MUTED);
    write(model.generatedAt, right, 71, { align: 'right' });

    stroke(RULE);
    doc.setLineWidth(0.75);
    rule(M, HEADER_RULE_Y, right, HEADER_RULE_Y);
  };

  // ── Title block ───────────────────────────────────────────────────────────
  const titleBlock = () => {
    if (model.memoNumber) {
      face('bold', 8);
      ink(BRAND);
      write(model.memoNumber.toUpperCase(), M, pen.y, { charSpace: 1.1 });
      pen.y += 15;
    }

    face('bold', 20);
    ink(NAVY);
    for (const line of wrap(model.title, CONTENT_W)) {
      room(25);
      write(line, M, pen.y + 16);
      pen.y += 25;
    }
    pen.y += 8;

    let x = M;
    if (model.classification) {
      const [f, i] = CLASS_PILL[model.classification] || [[241, 245, 249], [71, 85, 105]];
      x += pill(model.classification, x, pen.y, f, i);
    }
    const st = MEMO_STATUS[model.status];
    if (st) pill(st[0], x, pen.y, st[1], st[2]);
    pen.y += 16 + 16;
  };

  // ── At-a-glance band ──────────────────────────────────────────────────────
  const glanceBand = () => {
    const cols = model.glance;
    if (!cols.length) return;
    const h = 52;
    fill(PANEL);
    rbox(M, pen.y, CONTENT_W, h, 6);
    const colW = CONTENT_W / cols.length;
    cols.forEach((g, i) => {
      const x = M + i * colW + 12;
      face('bold', 6.8);
      ink(MUTED);
      write(g.label.toUpperCase(), x, pen.y + 17, { charSpace: 0.7 });
      face('bold', 9);
      ink(INK);
      write(wrap(g.value, colW - 20).slice(0, 2), x, pen.y + 31);
    });
    pen.y += h + 22;
  };

  // ── A comment thread, drawn against a left rail like the screen's ─────────
  const thread = (list, x, w) => {
    list.forEach((c, i) => {
      if (i > 0) pen.y += 9;
      room(34);
      const top = pen.y;
      avatar(c.initials, c.color, x + 8.5, pen.y + 8, 8.5);

      face('bold', 8.5);
      ink(INK);
      write(c.author, x + 24, pen.y + 11);
      const nameW = doc.getTextWidth(c.author);
      if (c.when) {
        face('normal', 7.5);
        ink(FAINT);
        write(c.when, x + 24 + nameW + 7, pen.y + 11);
      }
      pen.y += 17;
      para(c.body, x + 24, w - 24, { size: 8.5, color: BODY });

      // The rail is drawn after the body so it spans the height the body took.
      if (pen.y > top + 20 && !pen.dry) {
        stroke(HAIR);
        doc.setLineWidth(1.5);
        rule(x + 8.5, top + 19, x + 8.5, pen.y - 4);
      }
    });
  };

  // ── One action. Drawn identically in measure and paint passes. ────────────
  const actionBlock = (a) => {
    const padX = 14;
    const x = M + padX;
    const w = CONTENT_W - padX * 2;
    const top = pen.y;
    const [label, sInk, sFill] = ACT_STATUS[a.status] || ACT_STATUS.pending;

    pen.y += 13;

    // Number badge, tinted with the action's own status so the state of the
    // work reads from the left margin at a glance.
    fill(sFill);
    dot(x + 8, pen.y + 5, 8.5);
    face('bold', 8);
    ink(sInk);
    write(String(a.n), x + 8, pen.y + 8, { align: 'center' });

    // Status and due date, right-aligned on the title's first line.
    face('bold', 8);
    const statusText = label + (a.due ? ` · due ${a.due}` : '');
    const statusW = doc.getTextWidth(statusText) + 6;
    ink(sInk);
    write(statusText, x + w, pen.y + 8, { align: 'right' });

    // Title. The status text's width is reserved on every line rather than
    // just the first: re-wrapping a tail would have to rejoin already-broken
    // lines, which mangles any title carrying its own line breaks.
    face('bold', 11);
    ink(NAVY);
    const titleX = x + 24;
    const titleW = w - 24;
    wrap(a.title, Math.max(120, titleW - statusW)).forEach((line, i) => {
      if (i > 0) room(15);
      write(line, titleX, pen.y + 9);
      pen.y += 15;
    });
    pen.y += 3;

    // Owner, department, supporting departments, applicability.
    let mx = titleX;
    const metaY = pen.y;
    if (a.owner) {
      avatar(a.ownerInitials, a.ownerColor, mx + 6.5, metaY + 6, 6.5);
      face('bold', 8.5);
      ink(INK);
      write(a.owner, mx + 17, metaY + 9);
      mx += 17 + doc.getTextWidth(a.owner) + 10;
    } else {
      mx += pill('No owner assigned', mx, metaY, ROSE_BG, ROSE_INK, { size: 7.5 });
    }
    if (a.department) {
      face('normal', 8.5);
      ink(MUTED);
      write(a.department, mx, metaY + 9);
      mx += doc.getTextWidth(a.department) + 10;
    }
    for (const d of a.supporting) {
      mx += pill(`+ ${d}`, mx, metaY, [234, 245, 253], [3, 105, 161], { size: 7.5 });
    }
    if (a.applicability) {
      pill(a.applicability, mx, metaY, AMBER_BG, AMBER_INK, { size: 7.5 });
    }
    pen.y = metaY + 16;

    if (a.alsoOn.length) {
      pen.y += 2;
      para(`Also on: ${a.alsoOn.join(', ')}`, titleX, titleW, { size: 8, color: MUTED });
    }

    if (a.appliesIf) {
      pen.y += 4;
      face('normal', 8.5);
      const lines = wrap(`Applies only if: ${a.appliesIf}`, titleW - 20);
      const h = lines.length * lh(8.5) + 12;
      room(h);
      fill(AMBER_BG);
      stroke(AMBER_LINE);
      doc.setLineWidth(0.75);
      rbox(titleX, pen.y, titleW, h, 4, 'FD');
      face('normal', 8.5);
      ink(AMBER_INK);
      write(lines, titleX + 10, pen.y + 14);
      pen.y += h + 2;
    }

    if (a.whatToDo) {
      pen.y += 5;
      para(a.whatToDo, titleX, titleW, { size: 9, color: BODY });
    }

    if (a.quote) {
      pen.y += 6;
      const quoted = `“${a.quote}”${a.quotePage ? ` (p. ${a.quotePage})` : ''}`;
      const qTop = pen.y;
      para(quoted, titleX + 12, titleW - 12, { size: 8.5, style: 'italic', color: MUTED });
      // Skipped when the quote broke across a page: pen.y is then above qTop
      // and the rail would run backwards up the new page.
      if (pen.y > qTop) {
        stroke(RULE);
        doc.setLineWidth(1.5);
        rule(titleX + 2, qTop + 2, titleX + 2, pen.y - 3);
      }
      face('bold', 7);
      ink(FAINT);
      room(10);
      write('QUOTED FROM THE MEMO', titleX + 12, pen.y + 6, { charSpace: 0.6 });
      pen.y += 12;
    }

    if (a.evidenceHint) {
      pen.y += 3;
      para(`Evidence that closes this: ${a.evidenceHint}`, titleX, titleW, { size: 8, color: MUTED });
    }

    if (a.evidence.length) {
      pen.y += 6;
      room(16);
      let ex = titleX;
      for (const name of a.evidence) {
        face('bold', 7.5);
        const cw = doc.getTextWidth(name) + 15;
        if (ex + cw > titleX + titleW) { ex = titleX; pen.y += 17; room(16); }
        pill(name, ex, pen.y, GREEN_BG, GREEN_INK, { size: 7.5 });
        ex += cw + 6;
      }
      pen.y += 17;
    }

    if (a.comments.length) {
      pen.y += 8;
      room(14);
      stroke(HAIR);
      doc.setLineWidth(0.75);
      rule(titleX, pen.y, x + w, pen.y);
      pen.y += 10;
      thread(a.comments, titleX, titleW);
    }

    pen.y += 14;
    return pen.y - top;
  };

  /**
   * Measure a block by running its own draw code with the pen muted, then
   * decide where it goes. A block that fits on a fresh page is kept whole
   * inside a card; a block taller than any page flows instead, marked with a
   * left rail so it still reads as one action.
   */
  const measureAction = (a) => {
    const savedY = pen.y;
    const savedDry = pen.dry;
    const savedBreaks = pen.breaks;
    pen.dry = true;
    pen.breaks = false;
    pen.y = 0;
    const h = actionBlock(a);
    pen.dry = savedDry;
    pen.breaks = savedBreaks;
    pen.y = savedY;
    return h;
  };

  const placeAction = (a) => {
    const h = measureAction(a);
    const pageRoom = BODY_BOTTOM - CONT_TOP;
    if (h <= pageRoom) {
      if (pen.y + h > BODY_BOTTOM) newPage();
      fill(CARD);
      stroke(RULE);
      doc.setLineWidth(0.75);
      rbox(M, pen.y, CONTENT_W, h, 7, 'FD');
      actionBlock(a);
      pen.y += 8;
    } else {
      const top = pen.y;
      const startPage = doc.getNumberOfPages();
      actionBlock(a);
      if (doc.getNumberOfPages() === startPage) {
        stroke(RULE);
        doc.setLineWidth(2);
        rule(M + 1, top + 6, M + 1, pen.y - 10);
      }
      pen.y += 8;
    }
  };

  // ── Compose ───────────────────────────────────────────────────────────────
  masthead();
  titleBlock();
  glanceBand();

  if (model.briefing) {
    section('Briefing', 60);
    // The face is set before wrapping, always: splitTextToSize measures with
    // whatever font is currently active, so wrapping under the section label's
    // 7.5pt bold and then drawing at 9.5pt overruns the panel.
    face('normal', 9.5);
    const lines = wrap(model.briefing, CONTENT_W - 28);
    const h = lines.length * lh(9.5) + 20;
    room(Math.min(h, BODY_BOTTOM - CONT_TOP));
    if (h <= BODY_BOTTOM - CONT_TOP) {
      fill(PANEL);
      rbox(M, pen.y, CONTENT_W, h, 6);
      fill(BRAND);
      rbox(M, pen.y, 3, h, 1.5);
      face('normal', 9.5);
      ink(INK);
      write(lines, M + 16, pen.y + 20);
      pen.y += h + 6;
    } else {
      para(model.briefing, M + 16, CONTENT_W - 28, { size: 9.5, color: INK, gap: 6 });
    }
  }

  if (model.risk) {
    face('normal', 9);
    const lines = wrap(`If this is missed: ${model.risk}`, CONTENT_W - 28);
    const h = lines.length * lh(9) + 18;
    if (h <= BODY_BOTTOM - CONT_TOP) {
      room(h);
      fill(AMBER_BG);
      stroke(AMBER_LINE);
      doc.setLineWidth(0.75);
      rbox(M, pen.y, CONTENT_W, h, 6, 'FD');
      face('normal', 9);
      ink(AMBER_INK);
      write(lines, M + 14, pen.y + 18);
      pen.y += h + 8;
    } else {
      para(`If this is missed: ${model.risk}`, M + 14, CONTENT_W - 28, { size: 9, color: AMBER_INK, gap: 8 });
    }
  }
  if (model.briefing || model.risk) pen.y += 8;

  section('Summary');
  if (model.summary.length) {
    for (const s of model.summary) {
      room(lh(9.5));
      fill(BRAND);
      dot(M + 3, pen.y + 6, 1.8);
      para(s, M + 12, CONTENT_W - 12, { size: 9.5, color: BODY, gap: 4 });
    }
  } else {
    emptyLine();
  }
  pen.y += 12;

  // The heading only lands on a page that can also host the start of the first
  // action, capped so one very tall action does not push the heading forward on
  // its own.
  section(
    model.actions.length
      ? `Actions · ${model.approved} of ${model.actions.length} approved`
      : 'Actions',
    model.actions.length ? Math.min(measureAction(model.actions[0]), 190) : 30,
  );
  if (model.actions.length) {
    model.actions.forEach(placeAction);
  } else {
    emptyLine();
  }
  pen.y += 10;

  if (model.discussion) {
    section(model.discussion.label);
    if (model.discussion.comments.length) thread(model.discussion.comments, M, CONTENT_W);
    else emptyLine();
    pen.y += 12;
  }

  section('Documents on file');
  if (model.documents.length) {
    for (const d of model.documents) {
      room(18);
      face('normal', 9);
      ink(BODY);
      write(d.name, M + 2, pen.y + 8);
      let dx = M + 2 + doc.getTextWidth(d.name) + 8;
      if (d.isSource) dx += pill('Analysed source', dx, pen.y - 2, [255, 244, 234], [194, 65, 12], { size: 7 });
      if (d.size) {
        face('normal', 8);
        ink(FAINT);
        write(d.size, dx, pen.y + 8);
      }
      pen.y += 16;
      stroke(HAIR);
      doc.setLineWidth(0.5);
      rule(M, pen.y, M + CONTENT_W, pen.y);
      pen.y += 6;
    }
  } else {
    emptyLine();
  }

  pen.y += 14;
  room(24);
  para(
    `Printed on ${model.generatedAt}${model.generatedBy ? ` by ${model.generatedBy}` : ''}. The chained audit trail in the platform is the system of record.`,
    M, CONTENT_W, { size: 7.5, color: FAINT },
  );

  // ── Running header and footer, stamped once the page count is known. ──────
  const pageCount = doc.getNumberOfPages();
  for (let p = 1; p <= pageCount; p += 1) {
    doc.setPage(p);

    if (p > 1) {
      face('bold', 7.5);
      ink(BRAND);
      doc.text(model.memoNumber || 'MEMO RECORD', M, 58, { charSpace: 0.8 });
      face('normal', 7.5);
      ink(MUTED);
      let head = doc.splitTextToSize(model.title, CONTENT_W - 150)[0] || '';
      if (head.length < model.title.length) head = `${head.replace(/\s+$/, '')}…`;
      doc.text(head, M + CONTENT_W, 58, { align: 'right' });
      stroke(RULE);
      doc.setLineWidth(0.75);
      doc.line(M, 68, M + CONTENT_W, 68);
    }

    stroke(RULE);
    doc.setLineWidth(0.5);
    doc.line(M, PAGE_H - 44, M + CONTENT_W, PAGE_H - 44);
    face('normal', 7);
    ink(MUTED);
    doc.text(
      [model.orgName, model.memoNumber, 'Confidential'].filter(Boolean).join('  ·  '),
      M, PAGE_H - 32,
    );
    doc.text(`Page ${p} of ${pageCount}`, M + CONTENT_W, PAGE_H - 32, { align: 'right' });
  }

  return doc;
}

// ---------------------------------------------------------------------------
// Entry point — gathers the client name and the Deck Studio logo, renders,
// and saves. Called from the memo view's Print / PDF button.
// ---------------------------------------------------------------------------

export async function exportMemoPdf(data, { apiBase = '', generatedBy = '' } = {}) {
  const API = apiBase || process.env.REACT_APP_API_URL || '';
  const { memo, actions = [], comments = [], documents = [] } = data;

  let orgName = '';
  try {
    const r = await fetch(`${API}/api/organizations`, { credentials: 'include' });
    if (r.ok) { const d = await r.json(); orgName = d.organizations?.[0]?.name || ''; }
  } catch { /* the name is optional */ }

  // Inlined as a data URI rather than referenced by URL: the renderer needs the
  // bytes, and a cross-origin image request from the export would not carry the
  // session cookie.
  let logoDataUrl = null;
  try {
    const r = await fetch(`${API}/api/deck-studio/branding/logo`, { credentials: 'include' });
    if (r.ok) {
      const blob = await r.blob();
      if (blob.size > 0 && blob.type.startsWith('image/')) {
        logoDataUrl = await new Promise((resolve) => {
          const fr = new FileReader();
          fr.onload = () => resolve(fr.result);
          fr.onerror = () => resolve(null);
          fr.readAsDataURL(blob);
        });
      }
    }
  } catch { /* the logo is optional */ }

  const model = buildMemoModel({ memo, actions, comments, documents, orgName, logoDataUrl, generatedBy });
  const doc = renderMemoPdf(model);
  const slug = `${memo.memo_number || 'memo'}-${memo.title || ''}`
    .replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase().slice(0, 70);
  doc.save(`${slug || 'memo'}.pdf`);
}
